/**
 * Chef Roma dark funnel — single source of truth for mock + production tokens.
 * Mock keys mirror `artifacts/chef-roma-preview-local/css/styles.css` :root.
 * Production keys map to shadcn + Roma legacy vars in `tuscan-globals.css`.
 */

import type { PublicPageBrand } from "@/lib/public-brand-theme";

/**
 * Kill switch env var for the Chef Roma dark funnel.
 * Default ON (matches production chefsromakitchen.com). Set to `"false"` to
 * roll back to cream/burgundy. Still brand-gated via `shouldApplyRomaDarkFunnel`.
 */
export const ROMA_DARK_FUNNEL_ENV = "NEXT_PUBLIC_ROMA_DARK_FUNNEL" as const;

export function isRomaDarkFunnelEnabled(
  env: Partial<NodeJS.ProcessEnv> = process.env,
): boolean {
  return env[ROMA_DARK_FUNNEL_ENV] !== "false";
}

/** Mock funnel color tokens (dark Owner.com reference). */
export const romaFunnelColors = {
  bg: "#0d0d0d",
  card: "#1a1a1a",
  panel: "#1e1e1e",
  panel2: "#141414",
  brand: "#b91c1c",
  brandDark: "#a21818",
  brandLight: "#cf1f1f",
  redTextOnDark: "#ef6a5f",
  text: "#ffffff",
  textSecondary: "#d6d6d6",
  /** Body/muted on #0d0d0d — zinc-300, WCAG AA 4.5:1+. */
  textMuted: "#d4d4d4",
  /** Meta on #0d0d0d — zinc-400 floor. */
  textMeta: "#a3a3a3",
  star: "#f5b400",
  divider: "rgba(255, 255, 255, 0.10)",
  borderSoft: "rgba(255, 255, 255, 0.14)",
  borderStrong: "rgba(255, 255, 255, 0.26)",
} as const;

/** Typography roles — Bricolage headings, Hanken body (production keeps Hanken). */
export const romaFunnelTypography = {
  headingFontName: "Bricolage Grotesque",
  bodyFontName: "Hanken Grotesk",
  /** next/font variable wired in layout.tsx */
  headingFontVar: "--font-roma-heading",
  bodyFontVar: "--font-tuscan-sans",
  headingFallback: "Roma Heading Fallback",
  bodyFallback: "Tuscan Cove Sans Fallback",
} as const;

/** Geometry tokens from mock :root. */
export const romaFunnelGeometry = {
  radiusBtn: "8px",
  radiusCard: "14px",
  radiusPanel: "18px",
  containerMax: "1200px",
} as const;

/** Mock CSS custom property names (build-preview.mjs / styles.css). */
export const romaFunnelMockVarNames = {
  bg: "--bg",
  card: "--bg-card",
  panel: "--bg-panel",
  brand: "--brand",
  text: "--text",
  textMuted: "--text-muted",
  star: "--star",
  divider: "--divider",
  borderSoft: "--border-soft",
  borderStrong: "--border-strong",
  fontHeading: "--font-heading",
  fontBody: "--font-body",
  radiusBtn: "--r-btn",
  radiusCard: "--r-card",
  radiusPanel: "--r-panel",
  container: "--container",
} as const;

/** Production shadcn semantic vars (scoped under chefs-roma + data-roma-funnel). */
export const romaFunnelProductionShadcnVars = {
  background: "--background",
  foreground: "--foreground",
  card: "--card",
  cardForeground: "--card-foreground",
  popover: "--popover",
  popoverForeground: "--popover-foreground",
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  secondary: "--secondary",
  secondaryForeground: "--secondary-foreground",
  muted: "--muted",
  mutedForeground: "--muted-foreground",
  border: "--border",
  input: "--input",
  ring: "--ring",
} as const;

/** Production Roma legacy vars overridden when dark funnel is on. */
export const romaFunnelProductionRomaVars = {
  espresso: "--roma-espresso",
  charcoal: "--roma-charcoal",
  ink: "--roma-ink",
  ivory: "--roma-ivory",
  linen: "--roma-linen",
  bone: "--roma-bone",
  burgundy: "--roma-burgundy",
  burgundyDeep: "--roma-burgundy-deep",
  frame: "--roma-frame",
  parchmentLine: "--roma-parchment-line",
  saffron: "--roma-saffron",
  terracotta: "--roma-terracotta",
  redText: "--roma-red-text",
  star: "--roma-star",
  radius: "--roma-radius",
  radiusLg: "--roma-radius-lg",
} as const;

type CssVarMap = Record<string, string>;

/** Shadcn + Roma legacy values for the dark funnel selector block. */
export function getRomaFunnelProductionCssValues(): CssVarMap {
  const c = romaFunnelColors;
  return {
    [romaFunnelProductionShadcnVars.background]: c.bg,
    [romaFunnelProductionShadcnVars.foreground]: c.text,
    [romaFunnelProductionShadcnVars.card]: c.card,
    [romaFunnelProductionShadcnVars.cardForeground]: c.text,
    [romaFunnelProductionShadcnVars.popover]: c.panel,
    [romaFunnelProductionShadcnVars.popoverForeground]: c.text,
    [romaFunnelProductionShadcnVars.primary]: c.brand,
    [romaFunnelProductionShadcnVars.primaryForeground]: c.text,
    [romaFunnelProductionShadcnVars.secondary]: c.panel,
    [romaFunnelProductionShadcnVars.secondaryForeground]: c.text,
    [romaFunnelProductionShadcnVars.muted]: c.panel,
    [romaFunnelProductionShadcnVars.mutedForeground]: c.textMuted,
    [romaFunnelProductionShadcnVars.border]: c.borderSoft,
    [romaFunnelProductionShadcnVars.input]: c.borderSoft,
    [romaFunnelProductionShadcnVars.ring]: c.brand,
    [romaFunnelProductionRomaVars.espresso]: c.bg,
    [romaFunnelProductionRomaVars.charcoal]: c.card,
    [romaFunnelProductionRomaVars.ink]: c.panel,
    [romaFunnelProductionRomaVars.ivory]: c.bg,
    [romaFunnelProductionRomaVars.linen]: c.card,
    [romaFunnelProductionRomaVars.bone]: c.panel,
    [romaFunnelProductionRomaVars.burgundy]: c.brand,
    [romaFunnelProductionRomaVars.burgundyDeep]: c.brandDark,
    [romaFunnelProductionRomaVars.frame]: c.borderSoft,
    [romaFunnelProductionRomaVars.parchmentLine]: c.divider,
    [romaFunnelProductionRomaVars.saffron]: c.star,
    [romaFunnelProductionRomaVars.terracotta]: c.brand,
    [romaFunnelProductionRomaVars.redText]: c.redTextOnDark,
    [romaFunnelProductionRomaVars.star]: c.star,
    [romaFunnelProductionRomaVars.radius]: romaFunnelGeometry.radiusCard,
    [romaFunnelProductionRomaVars.radiusLg]: romaFunnelGeometry.radiusPanel,
  };
}

/** Emit mock `:root { ... }` block for build-preview.mjs (keeps mock in sync). */
export function emitCssRootBlock(): string {
  const c = romaFunnelColors;
  const g = romaFunnelGeometry;
  const t = romaFunnelTypography;
  const v = romaFunnelMockVarNames;

  const lines = [
    `:root {`,
    `  ${v.brand}: ${c.brand};`,
    `  --brand-dark: ${c.brandDark};`,
    `  --brand-light: ${c.brandLight};`,
    `  --red-text-on-dark: ${c.redTextOnDark};`,
    `  ${v.bg}: ${c.bg};`,
    `  ${v.card}: ${c.card};`,
    `  ${v.panel}: ${c.panel};`,
    `  --bg-panel-2: ${c.panel2};`,
    `  ${v.divider}: ${c.divider};`,
    `  ${v.borderSoft}: ${c.borderSoft};`,
    `  ${v.borderStrong}: ${c.borderStrong};`,
    `  ${v.text}: ${c.text};`,
    `  --text-secondary: ${c.textSecondary};`,
    `  ${v.textMuted}: ${c.textMuted};`,
    `  --text-meta: ${c.textMeta};`,
    `  ${v.star}: ${c.star};`,
    `  ${v.fontHeading}: "${t.headingFontName}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;`,
    `  ${v.fontBody}: "${t.bodyFontName}", system-ui, sans-serif;`,
    `  ${v.container}: ${g.containerMax};`,
    `  ${v.radiusBtn}: ${g.radiusBtn};`,
    `  ${v.radiusCard}: ${g.radiusCard};`,
    `  ${v.radiusPanel}: ${g.radiusPanel};`,
    `}`,
  ];

  return lines.join("\n");
}

/** Document attribute toggled by RomaDarkTheme when flag + brand match. */
export const ROMA_FUNNEL_DATA_ATTR = "data-roma-funnel" as const;
export const ROMA_FUNNEL_ON_VALUE = "on" as const;

/** @deprecated Use emitCssRootBlock */
export const emitMockRootCssBlock = emitCssRootBlock;

export function shouldApplyRomaDarkFunnel(
  pageBrand: string,
  env: Partial<NodeJS.ProcessEnv> = process.env,
): boolean {
  return pageBrand === "chefs-roma" && isRomaDarkFunnelEnabled(env);
}

/** Server-safe layout attribute for first-paint dark funnel on chefs-roma. */
export function getRomaFunnelLayoutAttribute(
  pageBrand: PublicPageBrand,
): typeof ROMA_FUNNEL_ON_VALUE | undefined {
  return shouldApplyRomaDarkFunnel(pageBrand) ? ROMA_FUNNEL_ON_VALUE : undefined;
}
